import {
  forwardRef,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';

import { cn } from './utils';

export type ControlSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Visual height. This intentionally replaces the native numeric `size` attribute. */
  size?: ControlSize;
  invalid?: boolean;
}

const controlSizeClasses: Record<ControlSize, string> = {
  sm: 'min-h-9 rounded-lg px-3 py-1.5 text-sm',
  md: 'min-h-10 rounded-xl px-3.5 py-2 text-sm',
  lg: 'min-h-12 rounded-xl px-4 py-2.5 text-base',
};

const baseControlClasses =
  'block w-full border bg-white text-slate-950 outline-none transition placeholder:text-slate-400 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 disabled:opacity-100';

const validControlClasses =
  'border-slate-200 shadow-sm shadow-slate-950/[0.02] hover:border-slate-300 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10';
const invalidControlClasses =
  'border-rose-400 bg-rose-50/30 focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, size = 'md', ...props },
  ref,
) {
  return (
    <input
      {...props}
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        baseControlClasses,
        controlSizeClasses[size],
        invalid ? invalidControlClasses : validControlClasses,
        className,
      )}
    />
  );
});

Input.displayName = 'Input';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  resize?: 'none' | 'vertical' | 'both';
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid = false, resize = 'vertical', ...props },
  ref,
) {
  return (
    <textarea
      {...props}
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        baseControlClasses,
        'min-h-24 rounded-xl px-3.5 py-2.5 text-sm leading-6',
        resize === 'none' ? 'resize-none' : resize === 'both' ? 'resize' : 'resize-y',
        invalid ? invalidControlClasses : validControlClasses,
        className,
      )}
    />
  );
});

Textarea.displayName = 'Textarea';

export interface FieldProps extends HTMLAttributes<HTMLDivElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  required?: boolean;
}

/** A compact layout wrapper for inputs, selects, and textareas. */
export function Field({
  children,
  className,
  error,
  hint,
  htmlFor,
  label,
  required = false,
  ...props
}: FieldProps) {
  return (
    <div {...props} className={cn('space-y-1.5', className)}>
      {label ? (
        <FieldLabel htmlFor={htmlFor} required={required}>
          {label}
        </FieldLabel>
      ) : null}
      {children}
      {error ? <FieldError>{error}</FieldError> : hint ? <FieldHint>{hint}</FieldHint> : null}
    </div>
  );
}

export interface FieldLabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
}

export const FieldLabel = forwardRef<HTMLLabelElement, FieldLabelProps>(function FieldLabel(
  { children, className, required = false, ...props },
  ref,
) {
  return (
    <label {...props} ref={ref} className={cn('block text-sm font-medium text-ink-900', className)}>
      {children}
      {required ? (
        <span aria-hidden="true" className="ml-0.5 text-rose-500">
          *
        </span>
      ) : null}
    </label>
  );
});

FieldLabel.displayName = 'FieldLabel';

export const FieldHint = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  function FieldHint({ className, ...props }, ref) {
    return <p {...props} ref={ref} className={cn('text-xs leading-5 text-slate-500', className)} />;
  },
);

FieldHint.displayName = 'FieldHint';

export const FieldError = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  function FieldError({ className, ...props }, ref) {
    return (
      <p
        {...props}
        ref={ref}
        role="alert"
        className={cn('text-xs font-medium leading-5 text-rose-600', className)}
      />
    );
  },
);

FieldError.displayName = 'FieldError';

export interface InputGroupProps extends HTMLAttributes<HTMLDivElement> {
  invalid?: boolean;
}

/**
 * A presentational wrapper for controls with a leading or trailing adornment.
 * The child control should receive `rounded-xl` classes compatible with this group.
 */
export const InputGroup = forwardRef<HTMLDivElement, InputGroupProps>(function InputGroup(
  { children, className, invalid = false, ...props },
  ref,
) {
  return (
    <div
      {...props}
      ref={ref}
      className={cn(
        'relative flex items-center rounded-xl',
        invalid ? 'text-rose-500' : 'text-slate-400',
        className,
      )}
    >
      {children}
    </div>
  );
});

InputGroup.displayName = 'InputGroup';

export interface InputAdornmentProps extends HTMLAttributes<HTMLSpanElement> {
  position?: 'start' | 'end';
}

export const InputAdornment = forwardRef<HTMLSpanElement, InputAdornmentProps>(
  function InputAdornment({ children, className, position = 'start', ...props }, ref) {
    return (
      <span
        {...props}
        ref={ref}
        className={cn(
          'pointer-events-none absolute inset-y-0 z-10 inline-flex items-center',
          position === 'start' ? 'left-3.5' : 'right-3.5',
          className,
        )}
      >
        {children}
      </span>
    );
  },
);

InputAdornment.displayName = 'InputAdornment';
